import { Node, Edge } from '@xyflow/react';
import {
  WorkflowNodeData,
  TriggerNodeData,
  BranchNodeData,
  SwitchNodeData,
  MergeNodeData,
  StopNodeData,
  ActionNodeData,
  DelayNodeData,
  LoopNodeData,
  SerializedRule,
  SerializedCondition,
  SerializedAction,
  ACTION_TYPE_OPTIONS,
  BRANCH_FIELD_OPTIONS,
  BRANCH_HANDLE_TRUE,
  BRANCH_HANDLE_FALSE,
  SWITCH_HANDLE_DEFAULT,
  getSwitchCaseHandle,
  LOOP_HANDLE_BODY,
  LOOP_HANDLE_AFTER,
  // v2 types
  V2WorkflowDefinition,
  V2WorkflowNode,
  V2WorkflowEdge,
  V2SerializedRule,
  V2NodeType,
  V2TriggerConfig,
  V2IfBranchConfig,
  V2SwitchConfig,
  V2ActionConfig,
  V2DelayConfig,
  V2StopConfig,
  V2LoopConfig,
} from '../types';
import type { AutomationRule, Action, ConditionalConfig } from '../../../../services/workflow.service';

type FlowNode = Node<WorkflowNodeData>;

/**
 * Converts the React Flow node/edge graph into the AutomationRule JSON
 * that the workflow-service backend understands.
 *
 * Strategy:
 * - One TriggerNode defines the rule's triggerType and optional channel condition
 * - BranchNodes with dual outputs (Yes/No) create conditional actions with thenActions/elseActions
 * - ActionNodes + DelayNodes produce the actions array
 * - Creates a single rule with proper conditional structure
 */
export function serializeWorkflow(
  nodes: FlowNode[],
  edges: Edge[],
  ruleName: string,
): SerializedRule[] {
  const triggerNode = nodes.find((n) => (n.data as any).kind === 'trigger') as
    | Node<TriggerNodeData>
    | undefined;

  if (!triggerNode) return [];

  const triggerData = triggerNode.data as TriggerNodeData;

  // Build trigger conditions (channel filter, ticket type filter)
  const conditions: SerializedCondition[] = [];
  const channelCondition = buildChannelCondition(triggerData);
  if (channelCondition) conditions.push(channelCondition);

  // Add ticket type filter from trigger if specified
  if (triggerData.ticketTypeId) {
    conditions.push({
      field: 'entity.ticketTypeId',
      operator: 'equals',
      value: triggerData.ticketTypeId,
    });
  }

  // Collect all actions starting from trigger, handling branches as conditional actions
  const actions = collectActionsWithBranching(triggerNode.id, nodes, edges);

  return [
    {
      name: ruleName,
      triggerType: triggerData.eventType,
      triggerConditions: { logic: 'AND', conditions },
      actions,
      stopOnMatch: false,
      isActive: true,
    },
  ];
}

/**
 * Walk the graph from a start node, handling branches as conditional actions.
 * When encountering a branch node with two outputs, creates a conditional action
 * with thenActions (true path) and elseActions (false path).
 */
function collectActionsWithBranching(
  startNodeId: string,
  nodes: FlowNode[],
  edges: Edge[],
): SerializedAction[] {
  const actions: SerializedAction[] = [];
  const visited = new Set<string>();
  let currentId: string | null = startNodeId;

  while (currentId) {
    if (visited.has(currentId)) break;
    visited.add(currentId);

    // Find all outgoing edges from current node
    const outEdges = edges.filter((e) => e.source === currentId);
    if (outEdges.length === 0) break;

    // Get first outgoing edge to find next node
    const outEdge = outEdges[0];
    const nextNode = nodes.find((n) => n.id === outEdge.target);
    if (!nextNode) break;

    const data = nextNode.data as WorkflowNodeData;

    if (data.kind === 'branch') {
      // Branch node - build conditional action with both paths
      const conditionalAction = buildConditionalAction(nextNode, nodes, edges, new Set(visited));
      actions.push(conditionalAction);
      // After a branch, we stop linear traversal - the branch contains its sub-workflows
      break;
    } else if (data.kind === 'action') {
      const actionData = data as ActionNodeData;
      if (actionData.actionType) {
        actions.push({
          type: actionData.actionType,
          config: actionData.config,
        });
      }
    } else if (data.kind === 'delay') {
      const delayData = data as DelayNodeData;
      // Peek ahead to see if there's an action after delay
      const afterDelayEdge = edges.find((e) => e.source === nextNode.id);
      if (afterDelayEdge) {
        const afterDelay = nodes.find((n) => n.id === afterDelayEdge.target);
        if (afterDelay && (afterDelay.data as any).kind === 'action') {
          const ad = afterDelay.data as ActionNodeData;
          if (ad.actionType) {
            actions.push({
              type: ad.actionType,
              config: ad.config,
              delay: delayData.delaySeconds,
            });
          }
          visited.add(afterDelay.id);
          currentId = afterDelay.id;
          continue;
        }
      }
    } else if (data.kind === 'end') {
      break;
    }

    currentId = nextNode.id;
  }

  return actions;
}

/**
 * Build a conditional action from a branch node.
 * Collects thenActions from the 'true' handle and elseActions from the 'false' handle.
 */
function buildConditionalAction(
  branchNode: FlowNode,
  nodes: FlowNode[],
  edges: Edge[],
  visited: Set<string>,
): SerializedAction {
  const branchData = branchNode.data as BranchNodeData;

  // Find edges from the true and false handles
  const trueEdge = edges.find(
    (e) => e.source === branchNode.id && e.sourceHandle === BRANCH_HANDLE_TRUE
  );
  const falseEdge = edges.find(
    (e) => e.source === branchNode.id && e.sourceHandle === BRANCH_HANDLE_FALSE
  );

  // Fallback for legacy branches without handle IDs - treat as true path only
  const legacyEdge = edges.find(
    (e) => e.source === branchNode.id && !e.sourceHandle
  );

  // Collect actions from each path
  const thenActions = trueEdge
    ? collectPathActions(trueEdge.target, nodes, edges, new Set(visited))
    : legacyEdge
      ? collectPathActions(legacyEdge.target, nodes, edges, new Set(visited))
      : [];

  const elseActions = falseEdge
    ? collectPathActions(falseEdge.target, nodes, edges, new Set(visited))
    : [];

  // Build the conditional action
  return {
    type: 'conditional',
    config: {},
    conditional: {
      condition: {
        logic: 'AND' as const,
        conditions: [
          {
            field: branchData.field,
            operator: branchData.operator,
            value: branchData.value,
          },
        ],
      },
      thenActions: thenActions.map((a) => ({
        type: a.type,
        config: a.config,
        delay: a.delay,
      })),
      elseActions: elseActions.length > 0
        ? elseActions.map((a) => ({
            type: a.type,
            config: a.config,
            delay: a.delay,
          }))
        : undefined,
    },
  };
}

/**
 * Collect actions along a single path (for then/else branches).
 * This is simpler linear collection that can handle nested branches.
 */
function collectPathActions(
  startNodeId: string,
  nodes: FlowNode[],
  edges: Edge[],
  visited: Set<string>,
): SerializedAction[] {
  const actions: SerializedAction[] = [];
  let currentId: string | null = startNodeId;

  while (currentId) {
    if (visited.has(currentId)) break;
    visited.add(currentId);

    const node = nodes.find((n) => n.id === currentId);
    if (!node) break;

    const data = node.data as WorkflowNodeData;

    if (data.kind === 'action') {
      const actionData = data as ActionNodeData;
      if (actionData.actionType) {
        actions.push({
          type: actionData.actionType,
          config: actionData.config,
        });
      }
    } else if (data.kind === 'delay') {
      const delayData = data as DelayNodeData;
      const nextEdge = edges.find((e) => e.source === node.id);
      if (nextEdge) {
        const nextNode = nodes.find((n) => n.id === nextEdge.target);
        if (nextNode && (nextNode.data as any).kind === 'action') {
          const ad = nextNode.data as ActionNodeData;
          if (ad.actionType) {
            actions.push({
              type: ad.actionType,
              config: ad.config,
              delay: delayData.delaySeconds,
            });
          }
          visited.add(nextNode.id);
          currentId = nextNode.id;
          continue;
        }
      }
    } else if (data.kind === 'branch') {
      // Nested branch - recurse with conditional action
      const conditionalAction = buildConditionalAction(node, nodes, edges, visited);
      actions.push(conditionalAction);
      break;
    } else if (data.kind === 'end') {
      break;
    }

    // Move to next node
    const outEdge = edges.find((e) => e.source === currentId);
    if (!outEdge) break;
    currentId = outEdge.target;
  }

  return actions;
}

function buildChannelCondition(triggerData: TriggerNodeData): SerializedCondition | null {
  if (!triggerData.channel || triggerData.channel === 'any') return null;
  return { field: 'entity.channel', operator: 'equals', value: triggerData.channel };
}

// ── Deserializer: AutomationRule JSON → React Flow nodes + edges ─────────────

const NODE_SPACING_Y = 150;
const NODE_SPACING_X = 300;
const NODE_X = 250;

/**
 * Converts an existing AutomationRule into React Flow nodes and edges
 * so the visual builder can render and edit it.
 * Now handles 'conditional' actions by creating branch nodes with dual outputs.
 */
export function deserializeRule(
  rule: AutomationRule,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): { nodes: FlowNode[]; edges: Edge[] } {
  const nodes: FlowNode[] = [];
  const edges: Edge[] = [];
  let y = 50;
  let idCounter = 0;
  const nextId = (prefix: string) => `${prefix}-${++idCounter}`;

  // --- 1. Trigger node ---
  const channelCondition = (rule.triggerConditions?.conditions ?? []).find(
    (c) => c.field === 'entity.channel',
  );
  const triggerId = nextId('trigger');
  nodes.push({
    id: triggerId,
    type: 'trigger',
    position: { x: NODE_X, y },
    data: {
      kind: 'trigger',
      label: 'Trigger',
      eventType: rule.triggerType,
      channel: (channelCondition?.value as string) || 'any',
    } as TriggerNodeData,
  });
  let prevId = triggerId;
  y += NODE_SPACING_Y;

  // --- 2. Process actions (including conditionals) ---
  // (The standalone "AI Classify" node was removed — ticket-type classification
  //  is configured via the dedicated email/AI flow, not as a builder node.)
  const result = deserializeActions(rule.actions, nodes, edges, prevId, y, NODE_X, nextId, options);

  // --- 4. End node ---
  const endId = nextId('end');
  nodes.push({
    id: endId,
    type: 'end',
    position: { x: NODE_X + 30, y: result.maxY + NODE_SPACING_Y },
    data: { kind: 'end', label: 'End' } as WorkflowNodeData,
  });
  // Connect the last action(s) to end
  for (const lastId of result.lastNodeIds) {
    edges.push({ id: `e-${lastId}-${endId}`, source: lastId, target: endId });
  }

  return { nodes, edges };
}

interface DeserializeResult {
  lastNodeIds: string[];
  maxY: number;
}

function deserializeActions(
  actions: Action[],
  nodes: FlowNode[],
  edges: Edge[],
  prevId: string,
  startY: number,
  xPos: number,
  nextId: (prefix: string) => string,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): DeserializeResult {
  let y = startY;
  let currentPrevId = prevId;
  const lastNodeIds: string[] = [];

  for (const action of actions) {
    // Handle delay
    if (action.delay && action.delay > 0) {
      const delayId = nextId('delay');
      nodes.push({
        id: delayId,
        type: 'delay',
        position: { x: xPos, y },
        data: { kind: 'delay', label: 'Delay', delaySeconds: action.delay } as DelayNodeData,
      });
      edges.push({ id: `e-${currentPrevId}-${delayId}`, source: currentPrevId, target: delayId, animated: true });
      currentPrevId = delayId;
      y += NODE_SPACING_Y;
    }

    // Handle conditional action (if/else branch)
    if (action.type === 'conditional' && action.conditional) {
      const branchResult = deserializeConditional(
        action.conditional,
        nodes,
        edges,
        currentPrevId,
        y,
        xPos,
        nextId,
        options,
      );
      y = branchResult.maxY + NODE_SPACING_Y;
      lastNodeIds.push(...branchResult.lastNodeIds);
      currentPrevId = ''; // Reset - paths may not converge
      continue;
    }

    // Regular action
    const actionId = nextId('action');
    const actionLabel = ACTION_TYPE_OPTIONS.find((o) => o.value === action.type)?.label || action.type;
    nodes.push({
      id: actionId,
      type: 'action',
      position: { x: xPos, y },
      data: {
        kind: 'action',
        label: actionLabel,
        actionType: action.type,
        config: { ...action.config },
      } as ActionNodeData,
    });
    if (currentPrevId) {
      edges.push({ id: `e-${currentPrevId}-${actionId}`, source: currentPrevId, target: actionId, animated: true });
    }
    currentPrevId = actionId;
    y += NODE_SPACING_Y;
  }

  if (currentPrevId && !lastNodeIds.includes(currentPrevId)) {
    lastNodeIds.push(currentPrevId);
  }

  return { lastNodeIds, maxY: y };
}

function deserializeConditional(
  conditional: ConditionalConfig,
  nodes: FlowNode[],
  edges: Edge[],
  prevId: string,
  startY: number,
  baseX: number,
  nextId: (prefix: string) => string,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): DeserializeResult {
  const y = startY;
  const lastNodeIds: string[] = [];

  // Create branch node
  const branchId = nextId('branch');
  const cond = conditional.condition.conditions[0];

  let displayLabel = cond ? `${cond.field} is ${cond.value}` : 'Condition';
  if (cond?.field === 'entity.ticketTypeId' && options?.ticketTypes) {
    const typeName = options.ticketTypes.find((t) => t.id === cond.value)?.name;
    if (typeName) displayLabel = `Ticket type is ${typeName}`;
  }

  nodes.push({
    id: branchId,
    type: 'branch',
    position: { x: baseX, y },
    data: {
      kind: 'branch',
      label: 'Branch',
      field: cond?.field || '',
      operator: cond?.operator || 'equals',
      value: String(cond?.value ?? ''),
      displayLabel,
    } as BranchNodeData,
  });
  edges.push({ id: `e-${prevId}-${branchId}`, source: prevId, target: branchId, animated: true });

  let maxY = y + NODE_SPACING_Y;

  // Deserialize "then" path (left side)
  if (conditional.thenActions && conditional.thenActions.length > 0) {
    const thenX = baseX - NODE_SPACING_X / 2;
    const firstThenId = `action-then-${branchId}`;

    // Connect branch to then path
    const thenResult = deserializePathActions(
      conditional.thenActions,
      nodes,
      edges,
      branchId,
      BRANCH_HANDLE_TRUE,
      y + NODE_SPACING_Y,
      thenX,
      nextId,
      options,
    );

    lastNodeIds.push(...thenResult.lastNodeIds);
    maxY = Math.max(maxY, thenResult.maxY);
  }

  // Deserialize "else" path (right side)
  if (conditional.elseActions && conditional.elseActions.length > 0) {
    const elseX = baseX + NODE_SPACING_X / 2;

    const elseResult = deserializePathActions(
      conditional.elseActions,
      nodes,
      edges,
      branchId,
      BRANCH_HANDLE_FALSE,
      y + NODE_SPACING_Y,
      elseX,
      nextId,
      options,
    );

    lastNodeIds.push(...elseResult.lastNodeIds);
    maxY = Math.max(maxY, elseResult.maxY);
  }

  // If no else actions, add the branch's false handle to last nodes (connects directly to end)
  if (!conditional.elseActions || conditional.elseActions.length === 0) {
    // We'll let the false path connect directly to the end node
    // For now, just don't add anything - the then path's last node will connect to end
  }

  return { lastNodeIds, maxY };
}

function deserializePathActions(
  actions: Action[],
  nodes: FlowNode[],
  edges: Edge[],
  branchId: string,
  handleId: string,
  startY: number,
  xPos: number,
  nextId: (prefix: string) => string,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): DeserializeResult {
  let y = startY;
  let currentPrevId = '';
  const lastNodeIds: string[] = [];
  let isFirstNode = true;

  for (const action of actions) {
    // Handle delay
    if (action.delay && action.delay > 0) {
      const delayId = nextId('delay');
      nodes.push({
        id: delayId,
        type: 'delay',
        position: { x: xPos, y },
        data: { kind: 'delay', label: 'Delay', delaySeconds: action.delay } as DelayNodeData,
      });

      if (isFirstNode) {
        // Connect from branch handle
        edges.push({
          id: `e-${branchId}-${delayId}-${handleId}`,
          source: branchId,
          sourceHandle: handleId,
          target: delayId,
          animated: true,
          label: handleId === BRANCH_HANDLE_TRUE ? 'Yes' : 'No',
          labelStyle: { fill: handleId === BRANCH_HANDLE_TRUE ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 10 },
          labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
        });
        isFirstNode = false;
      } else if (currentPrevId) {
        edges.push({ id: `e-${currentPrevId}-${delayId}`, source: currentPrevId, target: delayId, animated: true });
      }
      currentPrevId = delayId;
      y += NODE_SPACING_Y;
    }

    // Handle nested conditional
    if (action.type === 'conditional' && action.conditional) {
      const branchResult = deserializeConditional(
        action.conditional,
        nodes,
        edges,
        currentPrevId || branchId,
        y,
        xPos,
        nextId,
        options,
      );
      y = branchResult.maxY + NODE_SPACING_Y;
      lastNodeIds.push(...branchResult.lastNodeIds);
      currentPrevId = '';
      isFirstNode = false;
      continue;
    }

    // Regular action
    const actionId = nextId('action');
    const actionLabel = ACTION_TYPE_OPTIONS.find((o) => o.value === action.type)?.label || action.type;
    nodes.push({
      id: actionId,
      type: 'action',
      position: { x: xPos, y },
      data: {
        kind: 'action',
        label: actionLabel,
        actionType: action.type,
        config: { ...action.config },
      } as ActionNodeData,
    });

    if (isFirstNode) {
      // Connect from branch handle
      edges.push({
        id: `e-${branchId}-${actionId}-${handleId}`,
        source: branchId,
        sourceHandle: handleId,
        target: actionId,
        animated: true,
        label: handleId === BRANCH_HANDLE_TRUE ? 'Yes' : 'No',
        labelStyle: { fill: handleId === BRANCH_HANDLE_TRUE ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 10 },
        labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
      });
      isFirstNode = false;
    } else if (currentPrevId) {
      edges.push({ id: `e-${currentPrevId}-${actionId}`, source: currentPrevId, target: actionId, animated: true });
    }

    currentPrevId = actionId;
    y += NODE_SPACING_Y;
  }

  if (currentPrevId && !lastNodeIds.includes(currentPrevId)) {
    lastNodeIds.push(currentPrevId);
  }

  return { lastNodeIds, maxY: y };
}

// ══════════════════════════════════════════════════════════════════════════════
// v2 SERIALIZER: React Flow nodes/edges → V2WorkflowDefinition
// Maps directly to visual nodes without embedding conditionals in actions
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Converts React Flow nodes/edges to v2 workflow definition.
 * This produces a flat node/edge structure that maps directly to the visual canvas.
 */
export function serializeWorkflowV2(
  nodes: FlowNode[],
  edges: Edge[],
  ruleName: string,
): V2SerializedRule | null {
  const triggerNode = nodes.find((n) => (n.data as WorkflowNodeData).kind === 'trigger') as
    | Node<TriggerNodeData>
    | undefined;

  if (!triggerNode) return null;

  const v2Nodes: V2WorkflowNode[] = [];
  const v2Edges: V2WorkflowEdge[] = [];

  // Convert each React Flow node to v2 format
  for (const node of nodes) {
    const data = node.data as WorkflowNodeData;
    const v2Node = convertToV2Node(node, data);
    if (v2Node) {
      v2Nodes.push(v2Node);
    }
  }

  // Convert edges
  for (const edge of edges) {
    v2Edges.push({
      id: edge.id,
      sourceNodeId: edge.source,
      targetNodeId: edge.target,
      sourceHandle: edge.sourceHandle || undefined,
      label: typeof edge.label === 'string' ? edge.label : undefined,
    });
  }

  const workflow: V2WorkflowDefinition = {
    version: '2.0',
    entryNodeId: triggerNode.id,
    nodes: v2Nodes,
    edges: v2Edges,
  };

  return {
    name: ruleName,
    schemaVersion: 2,
    workflow,
    stopOnMatch: false,
    isActive: true,
  };
}

/**
 * Convert a React Flow node to v2 WorkflowNode
 */
function convertToV2Node(node: FlowNode, data: WorkflowNodeData): V2WorkflowNode | null {
  const baseNode = {
    id: node.id,
    position: node.position,
  };

  switch (data.kind) {
    case 'trigger': {
      const triggerData = data as TriggerNodeData;
      const conditions: SerializedCondition[] = [];

      // Add channel condition if specified
      if (triggerData.channel && triggerData.channel !== 'any') {
        conditions.push({
          field: 'entity.channel',
          operator: 'equals',
          value: triggerData.channel,
        });
      }

      // Add ticket type condition if specified
      if (triggerData.ticketTypeId) {
        conditions.push({
          field: 'entity.ticketTypeId',
          operator: 'equals',
          value: triggerData.ticketTypeId,
        });
      }

      const config: V2TriggerConfig = {
        eventType: triggerData.eventType,
        channel: triggerData.channel,
        conditions: conditions.length > 0
          ? { logic: 'AND', conditions }
          : undefined,
      };

      return {
        ...baseNode,
        type: 'trigger',
        label: triggerData.label,
        config,
      };
    }

    case 'branch': {
      const branchData = data as BranchNodeData;
      const config: V2IfBranchConfig = {
        condition: {
          logic: 'AND',
          conditions: [{
            field: branchData.field,
            operator: branchData.operator,
            value: branchData.value,
          }],
        },
      };

      return {
        ...baseNode,
        type: 'if_branch',
        label: branchData.displayLabel || branchData.label,
        config,
      };
    }

    case 'switch': {
      const switchData = data as SwitchNodeData;
      const config: V2SwitchConfig = {
        field: switchData.field,
        cases: switchData.cases.map((c, index) => ({
          value: c.value,
          handle: getSwitchCaseHandle(index),
          // Readable name (e.g. "Support / Assigned" for a stage id), so the
          // canvas still shows it after the workflow is reopened.
          ...(c.label && c.label !== String(c.value) ? { label: c.label } : {}),
        })),
        hasDefault: switchData.hasDefault,
      };

      return {
        ...baseNode,
        type: 'switch',
        label: switchData.displayLabel || switchData.label,
        config,
      };
    }

    case 'merge': {
      const mergeData = data as MergeNodeData;
      return {
        ...baseNode,
        type: 'merge',
        label: mergeData.label,
        config: {},
      };
    }

    case 'action': {
      const actionData = data as ActionNodeData;
      const config: V2ActionConfig = {
        actionType: actionData.actionType,
        config: actionData.config,
      };

      return {
        ...baseNode,
        type: 'action',
        label: actionData.displaySummary || actionData.label,
        config,
      };
    }

    case 'delay': {
      const delayData = data as DelayNodeData;
      const config: V2DelayConfig = {
        delaySeconds: delayData.delaySeconds,
      };

      return {
        ...baseNode,
        type: 'delay',
        label: delayData.label,
        config,
      };
    }

    case 'stop': {
      const stopData = data as StopNodeData;
      const config: V2StopConfig = {
        reason: stopData.reason,
      };

      return {
        ...baseNode,
        type: 'stop',
        label: stopData.label,
        config,
      };
    }

    case 'loop': {
      const loopData = data as LoopNodeData;
      const config: V2LoopConfig = {
        itemsPath: loopData.itemsPath,
        itemVariable: loopData.itemVariable,
        mode: loopData.mode,
        // Only include concurrency for the limited-parallel mode.
        ...(loopData.mode === 'parallel_limited' && loopData.concurrency
          ? { concurrency: loopData.concurrency }
          : {}),
        ...(loopData.maxIterations ? { maxIterations: loopData.maxIterations } : {}),
        continueOnError: loopData.continueOnError ?? true,
        outputVariable: loopData.outputVariable || 'loopResult',
      };

      return {
        ...baseNode,
        type: 'loop',
        label: loopData.displayLabel || loopData.label,
        config,
      };
    }

    case 'end': {
      return {
        ...baseNode,
        type: 'end',
        label: 'End',
        config: {},
      };
    }

    case 'classify': {
      // Classify is not a v2 node type - it's handled differently
      // For now, skip it or convert to an action
      return null;
    }

    default:
      return null;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// v2 DESERIALIZER: V2WorkflowDefinition → React Flow nodes/edges
// Creates visual nodes directly from the flat node/edge structure
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Converts a v2 workflow definition to React Flow nodes and edges.
 * This is a direct mapping since v2 already uses a flat node/edge structure.
 */
export function deserializeRuleV2(
  rule: V2SerializedRule,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): { nodes: FlowNode[]; edges: Edge[] } {
  const nodes: FlowNode[] = [];
  const edges: Edge[] = [];

  const workflow = rule.workflow;

  // Convert v2 nodes to React Flow nodes
  for (const v2Node of workflow.nodes) {
    const flowNode = convertFromV2Node(v2Node, options);
    if (flowNode) {
      nodes.push(flowNode);
    }
  }

  // Convert v2 edges to React Flow edges
  for (const v2Edge of workflow.edges) {
    const flowEdge = convertFromV2Edge(v2Edge);
    edges.push(flowEdge);
  }

  return { nodes, edges };
}

/**
 * Convert a v2 WorkflowNode to React Flow node
 */
function convertFromV2Node(
  v2Node: V2WorkflowNode,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): FlowNode | null {
  const position = v2Node.position || { x: 250, y: 0 };

  switch (v2Node.type) {
    case 'trigger': {
      const config = v2Node.config as V2TriggerConfig;

      // Extract channel and ticketTypeId from conditions
      let channel = config.channel || 'any';
      let ticketTypeId: string | undefined;
      let ticketTypeName: string | undefined;

      if (config.conditions?.conditions) {
        for (const cond of config.conditions.conditions) {
          if (cond.field === 'entity.channel' && typeof cond.value === 'string') {
            channel = cond.value;
          }
          if (cond.field === 'entity.ticketTypeId' && typeof cond.value === 'string') {
            ticketTypeId = cond.value;
            ticketTypeName = options?.ticketTypes?.find((t) => t.id === cond.value)?.name;
          }
        }
      }

      return {
        id: v2Node.id,
        type: 'trigger',
        position,
        data: {
          kind: 'trigger',
          label: v2Node.label || 'Trigger',
          eventType: config.eventType,
          channel,
          ticketTypeId,
          ticketTypeName,
        } as TriggerNodeData,
      };
    }

    case 'if_branch': {
      const config = v2Node.config as V2IfBranchConfig;
      const firstCondition = config.condition.conditions[0];

      let displayLabel = v2Node.label;
      if (!displayLabel && firstCondition) {
        displayLabel = `${firstCondition.field} ${firstCondition.operator} ${firstCondition.value}`;

        // Create friendly display for ticket type
        if (firstCondition.field === 'entity.ticketTypeId' && options?.ticketTypes) {
          const typeName = options.ticketTypes.find((t) => t.id === firstCondition.value)?.name;
          if (typeName) displayLabel = `Ticket type is ${typeName}`;
        }
      }

      return {
        id: v2Node.id,
        type: 'branch',
        position,
        data: {
          kind: 'branch',
          label: 'Branch',
          field: firstCondition?.field || '',
          operator: firstCondition?.operator || 'equals',
          value: String(firstCondition?.value ?? ''),
          displayLabel,
        } as BranchNodeData,
      };
    }

    case 'switch': {
      const config = v2Node.config as V2SwitchConfig;

      return {
        id: v2Node.id,
        type: 'switch',
        position,
        data: {
          kind: 'switch',
          label: v2Node.label || 'Switch',
          field: config.field,
          cases: config.cases.map((c) => ({
            value: c.value,
            label: c.label || String(c.value),
          })),
          hasDefault: config.hasDefault ?? false,
          displayLabel: v2Node.label,
        } as SwitchNodeData,
      };
    }

    case 'merge': {
      return {
        id: v2Node.id,
        type: 'merge',
        position,
        data: {
          kind: 'merge',
          label: v2Node.label || 'Merge',
        } as MergeNodeData,
      };
    }

    case 'action': {
      const config = v2Node.config as V2ActionConfig;
      const actionLabel = ACTION_TYPE_OPTIONS.find((o) => o.value === config.actionType)?.label || config.actionType;

      return {
        id: v2Node.id,
        type: 'action',
        position,
        data: {
          kind: 'action',
          label: actionLabel,
          actionType: config.actionType,
          config: config.config,
          displaySummary: v2Node.label,
        } as ActionNodeData,
      };
    }

    case 'delay': {
      const config = v2Node.config as V2DelayConfig;

      return {
        id: v2Node.id,
        type: 'delay',
        position,
        data: {
          kind: 'delay',
          label: v2Node.label || `Wait ${config.delaySeconds}s`,
          delaySeconds: config.delaySeconds,
        } as DelayNodeData,
      };
    }

    case 'stop': {
      const config = v2Node.config as V2StopConfig;

      return {
        id: v2Node.id,
        type: 'stop',
        position,
        data: {
          kind: 'stop',
          label: v2Node.label || 'Stop',
          reason: config.reason,
        } as StopNodeData,
      };
    }

    case 'loop': {
      const config = v2Node.config as V2LoopConfig;

      return {
        id: v2Node.id,
        type: 'loop',
        position,
        data: {
          kind: 'loop',
          label: v2Node.label || 'Loop',
          itemsPath: config.itemsPath,
          itemVariable: config.itemVariable,
          mode: config.mode || 'serial',
          concurrency: config.concurrency,
          maxIterations: config.maxIterations,
          continueOnError: config.continueOnError ?? true,
          outputVariable: config.outputVariable || 'loopResult',
          displayLabel: v2Node.label,
        } as LoopNodeData,
      };
    }

    case 'end': {
      return {
        id: v2Node.id,
        type: 'end',
        position,
        data: {
          kind: 'end',
          label: v2Node.label || 'End',
        } as WorkflowNodeData,
      };
    }

    case 'wait_for_event': {
      // For now, treat wait_for_event as a delay node
      return {
        id: v2Node.id,
        type: 'delay',
        position,
        data: {
          kind: 'delay',
          label: v2Node.label || 'Wait for Event',
          delaySeconds: 0,
        } as DelayNodeData,
      };
    }

    default:
      return null;
  }
}

/**
 * Convert a v2 WorkflowEdge to React Flow edge
 */
function convertFromV2Edge(v2Edge: V2WorkflowEdge): Edge {
  const edge: Edge = {
    id: v2Edge.id,
    source: v2Edge.sourceNodeId,
    target: v2Edge.targetNodeId,
    animated: true,
  };

  if (v2Edge.sourceHandle) {
    edge.sourceHandle = v2Edge.sourceHandle;

    // Add labels for branch edges
    if (v2Edge.sourceHandle === BRANCH_HANDLE_TRUE) {
      edge.label = 'Yes';
      edge.labelStyle = { fill: '#16a34a', fontWeight: 600, fontSize: 10 };
      edge.labelBgStyle = { fill: '#fff', fillOpacity: 0.9 };
    } else if (v2Edge.sourceHandle === BRANCH_HANDLE_FALSE) {
      edge.label = 'No';
      edge.labelStyle = { fill: '#dc2626', fontWeight: 600, fontSize: 10 };
      edge.labelBgStyle = { fill: '#fff', fillOpacity: 0.9 };
    } else if (v2Edge.sourceHandle === SWITCH_HANDLE_DEFAULT) {
      edge.label = 'Default';
      edge.labelStyle = { fill: '#6b7280', fontWeight: 600, fontSize: 10 };
      edge.labelBgStyle = { fill: '#fff', fillOpacity: 0.9 };
    } else if (v2Edge.sourceHandle.startsWith('case_')) {
      // For switch cases, use the provided label or show case number
      edge.label = v2Edge.label || v2Edge.sourceHandle.replace('case_', 'Case ');
      edge.labelStyle = { fill: '#3b82f6', fontWeight: 600, fontSize: 10 };
      edge.labelBgStyle = { fill: '#fff', fillOpacity: 0.9 };
    } else if (v2Edge.sourceHandle === LOOP_HANDLE_BODY) {
      edge.label = 'Each item';
      edge.labelStyle = { fill: '#0891b2', fontWeight: 600, fontSize: 10 };
      edge.labelBgStyle = { fill: '#fff', fillOpacity: 0.9 };
    } else if (v2Edge.sourceHandle === LOOP_HANDLE_AFTER) {
      edge.label = 'After';
      edge.labelStyle = { fill: '#6b7280', fontWeight: 600, fontSize: 10 };
      edge.labelBgStyle = { fill: '#fff', fillOpacity: 0.9 };
    }
  }

  // Override with custom label if provided
  if (v2Edge.label) {
    edge.label = v2Edge.label;
  }

  return edge;
}

/**
 * Determines the schema version of an automation rule.
 * Returns 2 if it has a v2 workflow definition, otherwise 1.
 */
export function getSchemaVersion(rule: AutomationRule): 1 | 2 {
  if (rule.schemaVersion === 2 || (rule as any).workflow?.version === '2.0') {
    return 2;
  }
  return 1;
}

/**
 * Universal deserializer that handles both v1 and v2 formats.
 * Detects the schema version and calls the appropriate deserializer.
 */
export function deserializeRuleUniversal(
  rule: AutomationRule,
  options?: { ticketTypes?: Array<{ id: string; name: string }> },
): { nodes: FlowNode[]; edges: Edge[] } {
  const version = getSchemaVersion(rule);

  if (version === 2 && (rule as any).workflow) {
    // Handle v2 format
    const v2Rule: V2SerializedRule = {
      name: rule.name,
      description: rule.description,
      schemaVersion: 2,
      workflow: (rule as any).workflow,
      stopOnMatch: rule.stopOnMatch,
      isActive: rule.isActive,
    };
    return deserializeRuleV2(v2Rule, options);
  }

  // Handle v1 format
  return deserializeRule(rule, options);
}
